/* -----------------UI--------------- */
import muiTheme from "@configs/muitheme";

/* -----------------Helpers & Hooks--------------- */
import { CommandBarProvider, useRegisterActions } from "@memorang/command-bar";
import { ThemeProvider } from "@mui/material";
import { ExamsFilterDialog } from "components/ExamsFilterDialog";
import { useRouter } from "expo-router";

import { events } from "@constants/tracking";
import { useThemeContext } from "@contexts/ThemeContext";
import { PurchaseDialog } from "@features/dashboard";
import { useExamContext } from "@features/exam";
import { SessionConfigDialog } from "@features/session-config";
import { hasProductAccess } from "@helpers/content/tag";
import useAnalytics from "@hooks/useAnalytics";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { useProductMetadata } from "@hooks/useProduct";
import { isDev } from "@memorang/constants";
import { DATE_FORMAT } from "@memorang/constants/src/date";
import { useToast } from "@memorang/ui";
/* -----------------Types--------------- */
import type { Action } from "kbar";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTimePicker } from "../index";
import { ModuleCommandBarActions } from "./ModuleCommandBarActions";

const DefaultCommandBarActions = () => {
	const router = useRouter();

	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);

	const { explicitCallTrackCustomEvent } = useAnalytics();

	const { toggleDarkMode } = useThemeContext();

	const [showExamsFilterDialog, setShowExamsFilterDialog] = useState(false);
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [showSessionConfigDialog, setShowSessionConfigDialog] = useState(false);

	const { data: productMetadata } = useProductMetadata();
	const hasAccess = isDev ? true : hasProductAccess(productMetadata);

	const {
		updateExamDate,
		currentExamContext: { examDate },
	} = useExamContext();

	const { t } = useTranslation();
	const { showToast } = useToast();

	const toggleShowExamsFilterDialog = () => {
		setShowExamsFilterDialog((prev) => !prev);
	};

	const handleTracking = (option: string) => {
		explicitCallTrackCustomEvent({
			eventName: events.commandBarOptionClicked,
			option,
		});
	};

	const actions: Action[] = [
		{
			name: t("commandBar.actions.home"),
			icon: "home-outline",
			keywords: "home, h",
			perform: () => {
				handleTracking("home");
				router.push("/(protected)/(tabs)/home");
			},
			id: "home",
		},
		...(hasAccess
			? []
			: [
					{
						name: t("commandBar.actions.premium"),
						icon: "crown-outline",
						keywords: "premium",
						perform: () => {
							handleTracking("premium");
							setShowPurchaseDialog(true);
						},
						id: "premium",
					},
				]),
		{
			name: t("commandBar.actions.progress"),
			icon: "chart-timeline-variant-shimmer",
			keywords: "progress",
			perform: () => {
				handleTracking("progress");
				router.push("/(protected)/(tabs)/progress");
			},
			id: "progress",
		},
		{
			name: t("commandBar.actions.setExamDate"),
			icon: "calendar-outline",
			keywords: "set test date",
			perform: () => {
				handleTracking("set-exam-date");
				setShowDatePicker(true);
			},
			id: "set-exam-date",
		},
		...(hasAccess
			? [
					{
						name: t("commandBar.actions.startSession"),
						icon: "play-outline",
						keywords: "start session",
						perform: () => {
							handleTracking("start-session");
							setShowSessionConfigDialog(true);
						},
						id: "start-session",
					},
				]
			: []),
		// {
		// 	name: "Change exam",
		// 	icon: "circle-edit-outline",
		// 	keywords: "change exam, ce",
		// 	shortcut: ["ce"],
		// 	perform: () => {
		// 		toggleShowExamsFilterDialog();
		// 	},
		// 	id: "exams",
		// },
		{
			name: t("commandBar.actions.changeTheme"),
			icon: "theme-light-dark",
			keywords: "change theme",
			perform: () => {
				handleTracking("change-theme");
				toggleDarkMode();
			},
			id: "theme",
		},
		// {
		// 	name: "Profile",
		// 	icon: "account-outline",
		// 	keywords: "profile",
		// 	shortcut: ["pf"],
		// 	perform: () => {
		// 		router.push("/profile");
		// 	},
		// 	id: "profile",
		// },
		// {
		// 	name: "Store",
		// 	icon: createIcon("cart-outline"),
		// 	keywords: "upgrade",
		// 	shortcut: ["u"],
		// 	perform: () => {
		// 		router.push("/store");
		// 	},
		// 	id: "store",
		// },
		{
			name: t("commandBar.actions.leaderboard"),
			icon: "trophy-outline",
			keywords: "leaderboard",
			perform: () => {
				handleTracking("leaderboard");
				router.push("/(protected)/(tabs)/leaderboard");
			},
			id: "leaderboard",
		},
		// {
		// 	name: "Redeem access code",
		// 	icon: createIcon("gift-outline"),
		// 	keywords: "redeem",
		// 	shortcut: ["r"],
		// 	perform: () => {
		// 		router.push("/redeem");
		// 	},
		// 	id: "redeem",
		// },
		// {
		// 	name: "Take assessment",
		// 	icon: createIcon("clipboard-check-outline"),
		// 	keywords: "assessment",
		// 	shortcut: ["ta"],
		// 	section: "Study",
		// 	perform: () => {
		// 		router.push("/practice-tests");
		// 	},
		// 	id: "assesment",
		// },
		//TODO:Revisit this
		// ...(showStudyPackViews
		//   ? [
		//       {
		//         name: 'Take study session',
		//         icon: (
		//           <MemorangIcons
		//             name="study-pack-outline"
		//             size={24}
		//             color={theme.colors.onSurfaceVariant}
		//           />
		//         ),
		//         keywords: 'study session',
		//         shortcut: ['ss'],
		//         section: 'Study',
		//         perform: () => {
		//           router.push('/study-pack/');
		//         },
		//         id: 'study-session',
		//       },
		//     ]
		//   : []),
		{
			name: t("commandBar.actions.settings"),
			icon: "cog-outline",
			keywords: "settings",
			section: "Account",
			perform: () => {
				handleTracking("settings");
				router.push("/account/settings");
			},
			id: "settings",
		},
	];
	useRegisterActions(actions);
	return (
		<>
			{showExamsFilterDialog && (
				<ExamsFilterDialog
					visible={showExamsFilterDialog}
					handleClose={toggleShowExamsFilterDialog}
				/>
			)}
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={() => setShowPurchaseDialog(false)}
					productType="STUDY_PACK"
				/>
			)}

			{showDatePicker ? (
				<DateTimePicker
					isVisible
					onCancel={() => setShowDatePicker(false)}
					initialDate={examDate}
					onConfirm={(date) => {
						setShowDatePicker(false);
						updateExamDate(date);
						const formattedDate =
							DateTime.fromJSDate(date).toFormat(DATE_FORMAT);
						showToast({
							message: t("account.examDateUpdated", { date: formattedDate }),
							duration: 1000,
						});
					}}
				/>
			) : null}
			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					handleClose={() => setShowSessionConfigDialog(false)}
				/>
			) : null}
		</>
	);
};
const CommandBarActions = () => {
	const demoExamId = useDemoExamStore((state) => state.currentExam.id);
	if (demoExamId === "proethica") {
		return <ModuleCommandBarActions />;
	}
	return <DefaultCommandBarActions />;
};

export const CommandBarProviderWrapper = ({
	children,
}: {
	children: React.ReactElement;
}) => {
	const { isDarkMode } = useThemeContext();
	const demoExamId = useDemoExamStore((state) => state.currentExam.id);
	const theme = muiTheme(isDarkMode);

	return (
		<ThemeProvider theme={theme}>
			<CommandBarProvider>
				<CommandBarActions
					key={`${demoExamId}-${isDarkMode ? "dark" : "light"}`}
				/>
				{children}
			</CommandBarProvider>
		</ThemeProvider>
	);
};
